/**
 * WebSocket Client
 *
 * Handles connection to blockchain data source and message processing.
 * Automatically reconnects on disconnect.
 */

export interface MiniblockMessage {
  block_number: number;
  index: number;
  tx_count?: number;
  transactions?: any[];
  receipts?: any[];
  gas_used?: string;
}

export interface BlockMessage {
  number: string | number;
  miniblock_count?: number;
  gas_used?: string;
  timestamp?: number;
}

export interface BlockUpdate {
  type: 'block_update';
  bn: number;          // Block number
  mbCount: number;     // Expected miniblock count
}

export interface ProjectMetrics {
  address: string;
  gasUsed: number;
  txCount: number;
}

// Full OLI label data
export interface OliLabelData {
  address: string;
  projectName?: string;
  contractName?: string;
  category?: string;
  source?: string;
}

export interface InitialStateMessage {
  timestamp: number;
  projects: ProjectMetrics[];
  globalBps: number;      // Blocks per second (rolling average) for immediate display
  globalTps?: number;     // Transactions per second (60s window)
  globalGps?: number;     // Gas per second (60s window)
  globalMbps?: number;    // Miniblocks per second (60s window)
  labels?: Record<string, string>;  // OLI labels: address -> projectName (for dApp grouping)
  labelsFull?: OliLabelData[];      // Full OLI label data (for display in modals)
}

export interface AddressActivity {
  addr: string;  // Contract address
  txc: number;   // Transaction count
  gas: number;   // Total gas used
}

export interface MiniblockUpdate {
  type: 'miniblock_update';
  seq: number;         // Sequence number
  bn: number;          // Block number
  idx: number;         // Miniblock index
  ts: number;          // Timestamp
  totalGas: number;    // Total gas used
  totalTx: number;     // Total transaction count
  bps: number;         // Backend blocks per second (1-min rolling average)
  ubps?: number;       // Upstream BPS - raw arrival rate from MegaETH WebSocket
  addrs: AddressActivity[];  // Address activity data
}

// NOTE: StatsUpdate removed - frontend now calculates stats locally from miniblock_update messages

export type MessageHandlers = {
  onMiniblock?: (data: MiniblockMessage) => void;
  onMiniblockUpdate?: (data: MiniblockUpdate) => void;
  onBlock?: (data: BlockMessage) => void;
  onBlockUpdate?: (data: BlockUpdate) => void;
  onInitialState?: (data: InitialStateMessage) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onStaleDataReset?: () => void; // Called before requesting fresh state (clear rolling window)
  onReconnecting?: (attempt: number, maxAttempts: number) => void; // Called when attempting to reconnect
  onError?: (error: string, context: string) => void; // Called on WebSocket errors
};

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectTimeout: number | null = null;
  private handlers: MessageHandlers = {};
  private url: string = '';

  // Heartbeat monitoring
  private lastMessageTime: number = 0;
  private messageCount: number = 0;
  private heartbeatInterval: number | null = null;

  // Reconnection tracking
  private reconnectAttempt: number = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 10;
  private readonly RECONNECT_DELAY_MS = 5000;

  // Address subscriptions (for saved circles - persists across reconnects)
  private subscribedAddresses: Set<string> = new Set();

  // Deferred message queue (for background tab optimization)
  private rawMessageQueue: string[] = [];
  private readonly MAX_QUEUE_SIZE = 500; // Limit memory usage
  private visibilityHandler: (() => void) | null = null;

  // Track when tab was hidden (for stale data detection)
  private tabHiddenTime: number = 0;
  private readonly STALE_THRESHOLD_MS = 60000; // 60 seconds - matches rolling window

  /**
   * Connect to WebSocket server
   */
  public connect(url: string, handlers: MessageHandlers): void {
    this.url = url;
    this.handlers = handlers;
    this.doConnect();
  }

  /**
   * Perform the actual connection
   */
  private doConnect(): void {
    if (!this.url) {
      console.error('No WebSocket URL configured');
      return;
    }

    // Set up visibility change handler (only once)
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.hidden) {
          // Track when tab was hidden
          this.tabHiddenTime = Date.now();
        } else {
          // Tab became visible - check if data is stale
          const hiddenDuration = this.tabHiddenTime > 0 ? Date.now() - this.tabHiddenTime : 0;

          if (hiddenDuration >= this.STALE_THRESHOLD_MS) {
            // Data is stale - request fresh state from backend
            // Clear the queue since we're getting fresh data anyway
            this.rawMessageQueue = [];
            this.requestFreshState();
          } else {
            // Data is still fresh - process queued messages normally
            this.processQueuedMessages();
          }

          this.tabHiddenTime = 0;
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    try {
      this.ws = new WebSocket(this.url);

      this.ws.addEventListener('open', () => {
        // Reset reconnect counter on successful connection
        this.reconnectAttempt = 0;

        this.startHeartbeat();

        // Re-subscribe to all saved addresses after reconnect (batch)
        if (this.subscribedAddresses.size > 0) {
          const addresses = Array.from(this.subscribedAddresses);
          this.ws!.send(JSON.stringify({ type: 'subscribe_batch', addresses }));
        }

        if (this.handlers.onConnected) {
          this.handlers.onConnected();
        }
      });

      this.ws.addEventListener('message', (event) => {
        this.handleMessage(event.data);
      });

      this.ws.addEventListener('error', (err) => {
        const errorMsg = 'WebSocket connection error';
        console.error(errorMsg, {
          url: this.url,
          reconnectAttempt: this.reconnectAttempt,
          lastMessageTime: this.lastMessageTime ? new Date(this.lastMessageTime).toISOString() : 'never'
        });
        if (this.handlers.onError) {
          this.handlers.onError(errorMsg, 'connection');
        }
      });

      this.ws.addEventListener('close', () => {
        this.stopHeartbeat();
        if (this.handlers.onDisconnected) {
          this.handlers.onDisconnected();
        }
        this.scheduleReconnect();
      });
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    this.stopHeartbeat();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Subscribe to an address for real-time updates (saved circles)
   */
  public subscribe(address: string): void {
    const normalizedAddr = address.toLowerCase();
    this.subscribedAddresses.add(normalizedAddr);
    this.sendSubscription('subscribe', normalizedAddr);
  }

  /**
   * Subscribe to multiple addresses at once (for initial load of saved circles)
   */
  public subscribeBatch(addresses: string[]): void {
    if (addresses.length === 0) return;

    const normalized = addresses.map(a => a.toLowerCase());
    for (const addr of normalized) {
      this.subscribedAddresses.add(addr);
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'subscribe_batch', addresses: normalized }));
    }
  }

  /**
   * Unsubscribe from an address (circle deleted/unsaved)
   */
  public unsubscribe(address: string): void {
    const normalizedAddr = address.toLowerCase();
    this.subscribedAddresses.delete(normalizedAddr);
    this.sendSubscription('unsubscribe', normalizedAddr);
  }

  /**
   * Send subscription message to server
   */
  private sendSubscription(type: 'subscribe' | 'unsubscribe', address: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, address }));
    }
  }

  /**
   * Schedule automatic reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) return;

    this.reconnectAttempt++;

    // Notify handlers about reconnection attempt
    if (this.handlers.onReconnecting) {
      this.handlers.onReconnecting(this.reconnectAttempt, this.MAX_RECONNECT_ATTEMPTS);
    }

    // Exponential backoff: 5s, 10s, 20s, 40s... capped at 60s
    const delay = Math.min(
      this.RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempt - 1),
      60000
    );

    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectTimeout = null;
      this.doConnect();
    }, delay);
  }

  /**
   * Force immediate reconnection (for user-triggered retry)
   */
  public forceReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempt = 0; // Reset attempts on manual retry
    this.doConnect();
  }

  /**
   * Get current reconnect attempt number
   */
  public getReconnectAttempt(): number {
    return this.reconnectAttempt;
  }

  /**
   * Start heartbeat monitoring
   */
  private startHeartbeat(): void {
    this.lastMessageTime = Date.now();
    this.messageCount = 0;

    // Check every 5 seconds if we're still receiving messages
    this.heartbeatInterval = window.setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;
      const secondsSinceLastMessage = Math.floor(timeSinceLastMessage / 1000);

      if (secondsSinceLastMessage > 30) {
        // Auto-reconnect if stalled for more than 30 seconds
        if (this.handlers.onError) {
          this.handlers.onError(`Connection stalled (${secondsSinceLastMessage}s without data)`, 'stalled');
        }
        this.ws?.close();
        // scheduleReconnect will be called by the close handler
      }
    }, 5000);
  }

  /**
   * Stop heartbeat monitoring
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    // Track message receipt for heartbeat (even when queued)
    this.lastMessageTime = Date.now();
    this.messageCount++;

    // Defer processing when tab is hidden to save CPU
    if (document.hidden) {
      this.rawMessageQueue.push(data);
      // Limit queue size to prevent memory issues
      if (this.rawMessageQueue.length > this.MAX_QUEUE_SIZE) {
        this.rawMessageQueue.shift();
      }
      return;
    }

    this.parseAndDispatch(data);
  }

  /**
   * Parse and dispatch a single message to handlers
   */
  private parseAndDispatch(data: string): void {
    try {
      const message = JSON.parse(data);

      if (message.type === 'initial_state' && this.handlers.onInitialState) {
        // Handle initial state message (sent on connection)
        const initialState: InitialStateMessage = {
          timestamp: message.timestamp,
          projects: message.projects,
          globalBps: message.globalBps || 0,
          globalTps: message.globalTps,
          globalGps: message.globalGps,
          globalMbps: message.globalMbps,
          labels: message.labels,           // OLI labels: address -> projectName
          labelsFull: message.labelsFull    // Full OLI label data
        };
        this.handlers.onInitialState(initialState);
      } else if (message.type === 'miniblock_update' && this.handlers.onMiniblockUpdate) {
        // New lightweight miniblock update (2-3 KB vs 100-250 KB!)
        this.handlers.onMiniblockUpdate(message as MiniblockUpdate);
      } else if (message.type === 'block_update' && this.handlers.onBlockUpdate) {
        // Minimal block update for miniblock verification
        this.handlers.onBlockUpdate(message as BlockUpdate);
      } else if (message.type === 'miniblock' && this.handlers.onMiniblock) {
        // Legacy format (for backward compatibility)
        this.handlers.onMiniblock(message.block as MiniblockMessage);
      } else if (message.type === 'block' && this.handlers.onBlock) {
        this.handlers.onBlock(message.block as BlockMessage);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Process queued messages when tab becomes visible
   */
  private processQueuedMessages(): void {
    if (this.rawMessageQueue.length === 0) return;

    // Process in batches to avoid blocking UI
    const BATCH_SIZE = 50;
    const processBatch = () => {
      const batch = this.rawMessageQueue.splice(0, BATCH_SIZE);
      for (const data of batch) {
        this.parseAndDispatch(data);
      }
      // Continue processing if more messages remain
      if (this.rawMessageQueue.length > 0) {
        requestAnimationFrame(processBatch);
      }
    };
    processBatch();
  }

  /**
   * Request fresh initial state from backend
   * Called when tab was hidden for too long, on pause resume, or when local data is stale
   */
  public requestFreshState(): void {
    // Notify handlers to clear stale data before we get fresh state
    if (this.handlers.onStaleDataReset) {
      this.handlers.onStaleDataReset();
    }

    // Request fresh state from backend
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'request_initial_state' }));
    }
  }
}
